import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { EquipmentBack } from "./EquipmentNavigation";
import { Dialog } from "@/components/ui/design-system";
import { errorMessage } from "@/lib/api";
import { loadEquipmentDecoder } from "@/lib/equipmentDecoder";
import {
  equipmentLabelsApi,
  equipmentMark,
  type ScannedEquipment,
} from "@/lib/api/equipmentLabels";

type Detector = {
  detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]>;
};
type DetectorClass = {
  new (options: { formats: string[] }): Detector;
  getSupportedFormats?: () => Promise<string[]>;
};
const permissionHelp =
  "U Chromeu dodirnite ikonu uz adresu → Dopuštenja → Kamera → Dopusti, zatim pokušajte ponovno. Možete i ručno upisati inventarni broj.";

export function EquipmentScanButton({
  onSelect,
}: {
  onSelect?: (piece: ScannedEquipment) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        <Icon name="scan" size={20} /> Skeniraj
      </Button>
      {open && (
        <Dialog
          fullscreen
          className="equipment-detail"
          title="Skeniraj opremu"
          onClose={() => setOpen(false)}
        >
          <div className="site-equipment">
            <EquipmentBack
              onBack={() => setOpen(false)}
              crumbs={[{ label: "Skeniraj" }]}
            />
            <EquipmentScanner
              onSelect={async (piece) => {
                if (onSelect) await onSelect(piece);
                else navigate(`/oprema/komadi/${piece.id}`);
                setOpen(false);
              }}
            />
          </div>
        </Dialog>
      )}
    </>
  );
}

export function EquipmentScanner({
  onSelect,
}: {
  onSelect: (piece: ScannedEquipment) => void | Promise<void>;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const manual = useRef<HTMLInputElement>(null);
  const manualId = useId();
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const resolving = useRef(false);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mark, setMark] = useState("");
  const [error, setError] = useState("");
  function stop() {
    generation.current += 1;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (video.current) video.current.srcObject = null;
  }
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stop();
      request.current?.abort();
    };
  }, []);

  async function select(value: string) {
    if (resolving.current) return;
    resolving.current = true;
    stop();
    setActive(false);
    setBusy(true);
    setError("");
    const controller = new AbortController();
    request.current?.abort();
    request.current = controller;
    try {
      const parsed = equipmentMark(value);
      const piece = await equipmentLabelsApi.resolve(
        parsed.value,
        parsed.qr,
        controller.signal,
      );
      if (!controller.signal.aborted && mounted.current) await onSelect(piece);
    } catch (cause) {
      if (!controller.signal.aborted && mounted.current)
        setError(
          errorMessage(
            cause,
            cause instanceof Error && !isAxiosError(cause)
              ? cause.message
              : "Komad nije pronađen ili nije dostupan.",
          ),
        );
    } finally {
      resolving.current = false;
      if (!controller.signal.aborted && mounted.current) setBusy(false);
    }
  }

  async function start() {
    stop();
    const current = generation.current;
    setError("");
    setActive(true);
    let decode: Awaited<ReturnType<typeof loadEquipmentDecoder>>;
    try {
      decode = await loadEquipmentDecoder();
    } catch {
      if (current !== generation.current || !mounted.current) return;
      stop();
      setActive(false);
      setError(
        "Čitač nije učitan. Provjerite internetsku vezu ili ručno upišite inventarni broj.",
      );
      return;
    }
    if (current !== generation.current || !mounted.current) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setActive(false);
      setError(
        "Kamera nije dostupna. Otvorite aplikaciju preko HTTPS-a ili upišite inventarni broj.",
      );
      return;
    }
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      if (current !== generation.current || !mounted.current) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = media;
      const element = video.current!;
      element.srcObject = media;
      await element.play();
      if (current !== generation.current || !mounted.current) return;
      const NativeDetector = (
        window as typeof window & { BarcodeDetector?: DetectorClass }
      ).BarcodeDetector;
      let detector: Detector | null = null;
      if (NativeDetector) {
        try {
          const formats = await NativeDetector.getSupportedFormats?.();
          if (!formats || formats.includes("qr_code"))
            detector = new NativeDetector({ formats: ["qr_code"] });
        } catch {
          /* Use the bundled decoder when native support is incomplete. */
        }
      }
      const canvas = document.createElement("canvas");
      let context = detector
        ? null
        : canvas.getContext("2d", { willReadFrequently: true });
      async function poll() {
        if (current !== generation.current || !mounted.current) return;
        try {
          let result: string | undefined;
          if (element.readyState >= 2 && element.videoWidth > 0) {
            if (detector) {
              try {
                result = (await detector.detect(element))[0]?.rawValue;
              } catch {
                detector = null;
                context = canvas.getContext("2d", { willReadFrequently: true });
              }
            } else if (context) {
              const scale = Math.min(1, 720 / element.videoWidth);
              canvas.width = Math.round(element.videoWidth * scale);
              canvas.height = Math.round(element.videoHeight * scale);
              context.drawImage(element, 0, 0, canvas.width, canvas.height);
              const frame = context.getImageData(
                0,
                0,
                canvas.width,
                canvas.height,
              );
              result = decode(frame.data, frame.width, frame.height, {
                inversionAttempts: "attemptBoth",
              })?.data;
            } else
              throw new Error("Kamera nije dostupna. Upišite inventarni broj.");
          }
          if (current !== generation.current || !mounted.current) return;
          if (result) {
            await select(result);
            return;
          }
          timer.current = setTimeout(() => void poll(), 150);
        } catch (cause) {
          if (current !== generation.current || !mounted.current) return;
          stop();
          setActive(false);
          setError(
            errorMessage(
              cause,
              "Kamera nije dostupna. Upišite inventarni broj.",
            ),
          );
        }
      }
      void poll();
    } catch (cause) {
      if (current !== generation.current || !mounted.current) return;
      stop();
      setActive(false);
      const name =
        cause && typeof cause === "object" && "name" in cause ? cause.name : "";
      setError(
        name === "NotAllowedError"
          ? "Pristup kameri nije dopušten. Dopustite kameru u postavkama preglednika ili upišite inventarni broj."
          : name === "NotFoundError"
            ? "Kamera nije pronađena na ovom uređaju. Upišite inventarni broj."
            : "Kamera nije dostupna. Pokušajte ponovno ili upišite inventarni broj.",
      );
    }
  }
  return (
    <div className="site-equipment equipment-scanner">
      <p>Skeniraj QR naljepnicu ili upiši broj s alata.</p>
      <div className="equipment-scanner-actions">
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            if (active) {
              stop();
              setActive(false);
            } else void start();
          }}
        >
          <Icon name="scan" size={24} />
          {active ? "Zaustavi kameru" : "Pokreni kameru"}
        </Button>
        <Button
          type="button"
          variant="outline"
          aria-controls={manualId}
          disabled={busy}
          onClick={() => {
            stop();
            setActive(false);
            manual.current?.focus();
          }}
        >
          <Icon name="pencil" /> Upiši inventarni broj
        </Button>
      </div>
      <video
        ref={video}
        muted
        playsInline
        aria-label="Kamera za QR naljepnice"
        hidden={!active}
        className="w-full rounded-lg"
      />

      <form
        className="equipment-step"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void select(mark);
        }}
      >
        <label className="grid gap-2">
          Inventarni broj
          <input
            id={manualId}
            ref={manual}
            value={mark}
            autoComplete="off"
            placeholder="INV-0001"
            onChange={(event) => setMark(event.target.value)}
          />
        </label>
        <Button type="submit" disabled={busy || !mark.trim()}>
          Pronađi komad
        </Button>
      </form>
      {busy && <p role="status">Tražim komad…</p>}
      {error && (
        <p role="alert" className="equipment-error">
          {error}
        </p>
      )}
      <p className="text-sm text-muted-foreground">{permissionHelp}</p>
    </div>
  );
}
